import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { flowRight, template } from "lodash";
import { Checkbox, CheckPicker, Heading, Message, toaster } from "rsuite";

import FunnelIcon from "@rsuite/icons/Funnel";
import SendIcon from "@rsuite/icons/Send";
import CopyIcon from "@rsuite/icons/Copy";

import {
    deleteErpPackage,
    loadErpPackages,
    loadErpPackageTargets,
    loadErpPackageTypes,
} from "api/deliverables/erp-packages-api";
import { getErrorMessage } from "api/defaults";

import {
    IErpPackage,
    IErpPackageTarget,
    IErpPackageType,
} from "store/deliverables/erp-packages/erp-packages-types";
import { AppDispatch, RootState } from "store/store";
import { setErrorResponseMessage } from "store/common/initialization/initialization-actions";

import constants from "utils/constants";
import { compareDates, isPreviousDate } from "utils/date-helper";

import useRefreshPage from "hooks/useRefreshPage";

import withLoader from "hocs/withLoader/withLoader";

import ErrorSection from "components/Common/ErrorSection/ErrorSection";

import EmailsSending from "./EmailsSending/EmailsSending";
import ErpPackageCard from "./ErpPackageCard/ErpPackageCard";
import ManageErpPackage from "./ManageErpPackage/ManageErpPackage";

import css from "./ErpPackages.module.css";

interface ComponentProps {
    showLoader: (isVisible: boolean) => void;
}

const ErpPackages: React.FC<ComponentProps> = ({
    showLoader,
}: ComponentProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const errorMessage = useSelector(
        (state: RootState) => state.initializationReducer.errorResponseMessage
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [packages, setPackages] = useState<IErpPackage[]>([]);
    const [filteredPackages, setFilteredPackages] = useState<IErpPackage[]>([]);
    const [isDataFetched, setDataFetched] = useState<boolean>(false);
    const [showExpired, setShowExpired] = useState<boolean>(true);
    const [packageTypes, setPackageTypes] = useState<IErpPackageType[]>([]);
    const [packageTargets, setPackageTargets] = useState<IErpPackageTarget[]>(
        []
    );
    const [checkedTypes, setCheckedTypes] = useState<string[]>([]);
    const [checkedTargets, setCheckedTargets] = useState<string[]>([]);
    const [checkedVersions, setCheckedVersions] = useState<string[]>([]);
    const [erpPackage, setErpPackage] = useState<IErpPackage | null>(null);
    const [isEmailsSendingOpened, setIsEmailsSendingOpened] =
        useState<boolean>(false);

    const title = t("deliverables.packages.title");

    const fetchData = () => {
        setPackages([]);
        dispatch(setErrorResponseMessage(""));
        showLoader(true);
        Promise.all([
            loadErpPackageTypes().then(([payload, statusCode]) => {
                if (payload && statusCode === constants.statusCode.OK) {
                    setPackageTypes(payload);
                } else {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        statusCode
                    );
                    toaster.push(
                        <Message showIcon type="error">
                            {errorMessage}
                        </Message>,
                        {
                            duration: constants.errorAlertDurationMsec,
                        }
                    );
                }
            }),
            loadErpPackageTargets().then(([payload, statusCode]) => {
                if (payload && statusCode === constants.statusCode.OK) {
                    setPackageTargets(payload);
                } else {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        statusCode
                    );
                    toaster.push(
                        <Message showIcon type="error">
                            {errorMessage}
                        </Message>,
                        {
                            duration: constants.errorAlertDurationMsec,
                        }
                    );
                }
            }),
            loadErpPackages().then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    setPackages(payload);
                    setFilteredPackages(payload);
                } else {
                    dispatch(
                        setErrorResponseMessage(
                            getErrorMessage(String(payload), status)
                        )
                    );
                }
            }),
        ]).then(() => {
            showLoader(false);
            setDataFetched(true);
        });
    };

    const deletePackage = (erpPackage: IErpPackage) => {
        showLoader(true);
        deleteErpPackage(erpPackage.id).then(([payload, status]) => {
            showLoader(false);
            setDataFetched(false);
            if (status === constants.statusCode.OK) {
                toaster.push(
                    <Message showIcon type="success">
                        {template(
                            t("deliverables.packages.delete.successMessage")
                        )({
                            packageVersion: erpPackage.version,
                        })}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            } else {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            }
        });
    };

    const renderFilterMenuItem = (label: ReactNode) => {
        return <div className={css.filterItem}>{label}</div>;
    };

    useEffect(() => {
        if (!isDataFetched) {
            fetchData();
        }
    }, [isDataFetched]);

    useEffect(() => {
        setFilteredPackages(
            packages.filter(
                (p) =>
                    (!checkedTypes.length ||
                        checkedTypes.includes(p.packageType.id)) &&
                    (!checkedTargets.length ||
                        checkedTargets.includes(p.packageTarget.id)) &&
                    (!checkedVersions.length ||
                        checkedVersions.includes(p.version)) &&
                    (showExpired || !isPreviousDate(new Date(p.expirationDate)))
            )
        );
    }, [checkedTypes, checkedTargets, checkedVersions, showExpired, packages]);

    useEffect(() => {
        if (packages.length && checkedVersions.length) {
            setCheckedVersions(
                checkedVersions.filter((checked) =>
                    packages.map((checked) => checked.version).includes(checked)
                )
            );
        }
    }, [packages]);

    useRefreshPage(() => {
        containerRef.current?.scrollTo(0, 0);
        fetchData();
    });

    if (errorMessage.length > 0) {
        return (
            <div className={css.error}>
                <ErrorSection errorMessage={errorMessage} />
            </div>
        );
    }

    return (
        <>
            <ManageErpPackage
                existingPackages={filteredPackages}
                packageTypes={packageTypes}
                packageTargets={packageTargets}
                refreshPackages={fetchData}
            />
            <EmailsSending
                erpPackage={erpPackage}
                refreshPackages={fetchData}
                isOpened={isEmailsSendingOpened}
                setIsEmailsSendingOpened={setIsEmailsSendingOpened}
            />
            {isDataFetched && (
                <>
                    <Heading level={1}>{title}</Heading>

                    <div className={css.filters}>
                        <FunnelIcon />
                        <CheckPicker
                            value={checkedTypes}
                            data={packageTypes.map((value) => {
                                return {
                                    label: value.type,
                                    value: value.id,
                                };
                            })}
                            placeholder={t("deliverables.packages.typeTitle")}
                            searchable={false}
                            renderMenuItem={renderFilterMenuItem}
                            onChange={setCheckedTypes}
                        />
                        <CheckPicker
                            value={checkedTargets}
                            data={packageTargets.map((value) => {
                                return {
                                    label: value.target,
                                    value: value.id,
                                };
                            })}
                            placeholder={t("deliverables.packages.targetTitle")}
                            searchable={false}
                            renderMenuItem={renderFilterMenuItem}
                            className={css.targetFilter}
                            onChange={setCheckedTargets}
                        />
                        <CheckPicker
                            value={checkedVersions}
                            data={Array.from(
                                new Set(packages.map((p) => p.version))
                            ).map((version) => ({
                                value: version,
                                label: version,
                            }))}
                            placeholder={t(
                                "deliverables.packages.versionTitle"
                            )}
                            searchable={false}
                            renderMenuItem={renderFilterMenuItem}
                            onChange={setCheckedVersions}
                        />
                        <Checkbox
                            defaultChecked
                            onChange={(_, checked) => {
                                setShowExpired(checked);
                            }}
                        >
                            <div>
                                {t("deliverables.packages.filterExpiredTitle")}
                            </div>
                        </Checkbox>
                    </div>
                    <div>
                        {t("deliverables.packages.explanationStarting")}
                        <SendIcon className={css.icon} />
                        {t("deliverables.packages.sendIconExplanation")}
                    </div>
                    <div>
                        {t("deliverables.packages.explanationStarting")}
                        <CopyIcon className={css.icon} />
                        {t("deliverables.packages.copyIconExplanation")}
                    </div>
                    {filteredPackages.length ? (
                        <div ref={containerRef} className={css.listContainer}>
                            {filteredPackages
                                .sort((p1, p2) => {
                                    return compareDates(
                                        new Date(p1.creationDate),
                                        new Date(p2.creationDate)
                                    );
                                })
                                .map(
                                    (
                                        erpPackage: IErpPackage,
                                        index: number
                                    ) => (
                                        <ErpPackageCard
                                            erpPackage={erpPackage}
                                            key={String(index)}
                                            deletePackage={deletePackage}
                                            setErpPackage={setErpPackage}
                                            setIsEmailsSendingOpened={
                                                setIsEmailsSendingOpened
                                            }
                                        />
                                    )
                                )}
                        </div>
                    ) : (
                        <div className={css.emptyContainer}>
                            {t("deliverables.packages.noDataFound")}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const Component = flowRight(withLoader)(ErpPackages);

export default Component;
